import { addDoc, collection, doc, serverTimestamp, setDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from './firebase';
import { localDemo } from './localDemo';

function requireDb() {
  if (!db) throw new Error('Firebase is not configured. Add your VITE_FIREBASE_* variables first.');
  return db;
}

export async function createMaster(collectionName: 'flowers' | 'suppliers' | 'customers' | 'accounts' | 'categories', data: Record<string, unknown>) {
  if (!db) return localDemo.createMaster(collectionName, data);
  const firestore = requireDb();
  return addDoc(collection(firestore, collectionName), { ...data, createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
}

export async function saveSettings(data: Record<string, unknown>) {
  if (!db) return localDemo.saveSettings(data);
  const firestore = requireDb();
  return setDoc(doc(firestore, 'settings', 'company'), { ...data, updatedAt: serverTimestamp() }, { merge: true });
}

export async function callFinance<T = unknown>(name: string, payload: unknown): Promise<T> {
  if (!functions) return localDemo.finance(name, payload) as T;
  const result = await httpsCallable<unknown, T>(functions, name)(payload);
  return result.data;
}

export async function uploadToCloudinary(file: File, folder: string): Promise<{ secureUrl: string; publicId: string }> {
  const cloudName = import.meta.env.VITE_CLOUDINARY_CLOUD_NAME;
  if (!cloudName) throw new Error('Cloudinary is not configured. Add VITE_CLOUDINARY_CLOUD_NAME first.');
  const unsignedPreset = import.meta.env.VITE_CLOUDINARY_UPLOAD_PRESET;
  const formData = new FormData();
  formData.append('file', file);

  // Firebase Functions require the Blaze plan. With a static deployment,
  // a restricted unsigned preset keeps uploads available without exposing a secret.
  if (unsignedPreset) {
    formData.append('upload_preset', unsignedPreset);
    formData.append('folder', folder);
  } else {
    const signature = await callFinance<{ timestamp: number; signature: string; apiKey: string; folder: string }>('createCloudinarySignature', { folder });
    formData.append('api_key', signature.apiKey);
    formData.append('timestamp', String(signature.timestamp));
    formData.append('signature', signature.signature);
    formData.append('folder', signature.folder);
  }
  const response = await fetch(`https://api.cloudinary.com/v1_1/${cloudName}/auto/upload`, { method: 'POST', body: formData });
  if (!response.ok) throw new Error('The attachment upload failed. Please try again.');
  const result = await response.json() as { secure_url?: string; public_id?: string };
  if (!result.secure_url || !result.public_id) throw new Error('Cloudinary did not return a file URL.');
  return { secureUrl: result.secure_url, publicId: result.public_id };
}
